/** @jsx jsx */
import { Fragment, useEffect, useState } from "react"
import { graphql } from "gatsby"
import PropTypes from "prop-types"
import { jsx, Styled } from "theme-ui"

import Variant from "@components/Variant"
import Border from "@src/components/Border"
import useLocaleContext from "@src/hooks/useLocaleContext"

import Loading from "./Loading/Loading"
import { formatNumber, formatTermPeriod } from "./helper"

export const fragment = graphql`
  fragment ContentfulDepositTermsBlock on ContentfulDepositTermsBlock {
    id
    title
    depositTermsUrl
  }
`

export default function DepositTerms({ title, depositTermsUrl }) {
  const locale = useLocaleContext()

  const [termsData, setTermsData] = useState([])
  const [isLoading, setIsLoading] = useState(true)

  const currentTerms =
    termsData.length > 0 ? (
      <div className="rates">
        <div className="rates-header" aria-hidden="true">
          <span>Bindningstid</span>
          <span>Aktuell ränta</span>
        </div>
        <dl>
          {termsData?.map(item => (
            <div key={item.name}>
              <dt>{formatTermPeriod(item)}</dt>
              <dd>
                {formatNumber(locale, item.interest_rate, {
                  style: "percent",
                })}
              </dd>
            </div>
          ))}
        </dl>
      </div>
    ) : (
      <p sx={{ fontSize: 3, textAlign: "center" }}>
        Just nu verkar våra aktuella räntor och bindningstider inte kunna visas,
        ladda om sidan eller försök igen senare.
      </p>
    )

  useEffect(() => {
    setIsLoading(true)
    fetch(depositTermsUrl)
      .then(response => response.json())
      .then(({ data }) => {
        const sortedData = data.sort((a, b) => {
          if (a.term_period === null) return -1
          if (b.term_period === null) return 1
          return a.term_period - b.term_period
        })
        setTermsData(sortedData)
        setIsLoading(false)
      })
      .catch(() => setIsLoading(false))
  }, [])

  return (
    <Fragment>
      <Variant
        variants={["container", "containerSpacing"]}
        d="grid"
        as="section"
        gap={0}
        sx={{
          flexDirection: "column",
          alignItems: "flex-start",
          textAlign: "left",
          fontSize: 3,
          lineHeight: "1.25",
          p: {
            margin: 0,
            "&:empty": {
              display: "none",
            },
          },
          ".rates": {
            display: "flex",
            flexDirection: ["column", "column", "row"],
            width: "100%",
            marginBottom: [5],
          },
          ".rates-header, dl > div": {
            display: "flex",
            flexDirection: ["row", "row", "column"],
            justifyContent: ["space-between", "space-between", "center"],
            gap: [0, 0, 3],
            padding: ["20px 16px", "28px 16px", "36px 16px"],
          },
          ".rates-header": {
            borderBottom: ["2px solid", "2px solid", "none"],
            borderRight: ["none", "none", "2px solid"],
            borderColor: "grey.light",
          },
          dl: {
            display: ["block", "block", "grid"],
            gridAutoFlow: "column",
            gridAutoColumns: "1fr",
            flex: 1,
            margin: 0,
          },
          "dl > div": {
            alignItems: "center",
            "&:nth-of-type(2n)": {
              backgroundColor: "rgba(245, 245, 245, 1)",
            },
          },
          dd: {
            margin: 0,
            fontWeight: 700,
          },
        }}
      >
        <Styled.h2 sx={{ textAlign: "center", marginBottom: [9, 13] }}>
          {title}
        </Styled.h2>
        {isLoading ? (
          <Variant
            variants={["container", "containerSpacing"]}
            d="flex"
            sx={{ justifyContent: "center", alignItems: "center" }}
          >
            <Loading />
          </Variant>
        ) : (
          <Variant>{currentTerms}</Variant>
        )}
      </Variant>
      <Border />
    </Fragment>
  )
}

DepositTerms.typeName = "ContentfulDepositTermsBlock"

DepositTerms.defaultProps = {
  title: "",
  depositTermsUrl: "",
}

DepositTerms.propTypes = {
  title: PropTypes.string.isRequired,
  depositTermsUrl: PropTypes.string.isRequired,
}
